import React, { useState, useMemo } from 'react';
import { ArrowDownLeft, ArrowUpRight, ChevronLeft } from 'lucide-react';
import { SmsExchange } from './SmsExchange';
import { useThreadSummaries } from '../../../hooks/support/useThreadSummaries';
import type { ContactPhone } from './types';

export interface SmsPanelProps {
  /** Contact phones to show threads for */
  contactPhones: ContactPhone[];
  /** Default branch for send-from number */
  defaultBranch?: string;
  /** Compact mode — less padding, smaller text */
  compact?: boolean;
  /** Show the To field so the user can select which contact to text */
  showToField?: boolean;
}

/** Format a Firestore timestamp as a relative time string. */
function formatRelativeTime(ts: { toDate: () => Date } | null): string {
  if (!ts) return '';
  const date = ts.toDate();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/**
 * SmsPanel — Multi-thread SMS wrapper.
 *
 * For a single phone, renders SmsExchange directly (no thread list).
 * For multiple phones, shows a thread summary list; clicking a row
 * opens SmsExchange for that thread.
 */
export function SmsPanel({ contactPhones, defaultBranch, compact = false, showToField = false }: SmsPanelProps) {
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);

  const phones = useMemo(
    () => contactPhones.map((c) => c.phone),
    [contactPhones]
  );

  // Only subscribe to thread summaries when showing the multi-thread list
  const shouldFetchSummaries = contactPhones.length > 1;
  const { summaries, loading } = useThreadSummaries(
    shouldFetchSummaries ? phones : []
  );

  // Single phone shortcut — render SmsExchange directly
  if (contactPhones.length === 1) {
    return (
      <SmsExchange
        externalPhone={contactPhones[0].phone}
        contactPhones={contactPhones}
        showToField={showToField}
        defaultBranch={defaultBranch}
        compact={compact}
      />
    );
  }

  // No contacts
  if (contactPhones.length === 0) {
    return (
      <p className="text-sm text-slate-400 italic p-3">
        No contact phone numbers available
      </p>
    );
  }

  // Thread selected — show SmsExchange with back button
  if (selectedPhone) {
    return (
      <div className="flex flex-col h-full min-h-0">
        <button
          onClick={() => setSelectedPhone(null)}
          className="flex items-center gap-1 px-3 py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors border-b border-slate-300"
        >
          <ChevronLeft className="w-3 h-3" />
          All threads ({contactPhones.length})
        </button>
        <div className="flex-1 min-h-0">
          <SmsExchange
            externalPhone={selectedPhone}
            contactPhones={contactPhones}
            showToField={false}
            defaultBranch={defaultBranch}
            compact={compact}
          />
        </div>
      </div>
    );
  }

  // Thread list
  return (
    <div className="divide-y divide-slate-100">
      {loading && (
        <div className="p-3 text-xs text-slate-400">Loading threads...</div>
      )}
      {contactPhones.map((contact) => {
        const phone10 = contact.phone.replace(/\D/g, '').slice(-10);
        const summary = summaries.get(phone10);
        const dirIcon = summary
          ? summary.lastMessageDirection === 'outbound'
            ? <ArrowUpRight className="w-3 h-3 text-blue-400 flex-shrink-0" />
            : <ArrowDownLeft className="w-3 h-3 text-green-500 flex-shrink-0" />
          : null;

        return (
          <button
            key={contact.phone}
            onClick={() => setSelectedPhone(contact.phone)}
            className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800 truncate">
                  {contact.label}
                </span>
                <span className="text-xs text-slate-400 font-mono flex-shrink-0">
                  {contact.phone.replace(/(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3')}
                </span>
              </div>
              {summary ? (
                <div className="flex items-center gap-1.5 mt-0.5">
                  {dirIcon}
                  <span className="text-xs text-slate-500 truncate">
                    {summary.lastMessageText || 'No messages'}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-slate-400 italic mt-0.5 block">
                  No messages yet
                </span>
              )}
            </div>
            {summary?.lastMessageAt && (
              <span className="text-[10px] text-slate-400 flex-shrink-0">
                {formatRelativeTime(summary.lastMessageAt)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
